import React from 'react';
import { WxbInput, WxbInputNumber, WxbSelect } from '../wxb-ui';
import type {
  EquipmentCalendarTemporalExpression,
  EquipmentDstGapPolicy,
  EquipmentDstOverlapPolicy,
  EquipmentElapsedTemporalExpression,
  EquipmentMonthEndPolicy,
  EquipmentTemporalExpression,
  EquipmentTemporalExpressionBasis,
} from '../../types/prodEquipmentStateCatalog';

interface Props {
  value: EquipmentTemporalExpression;
  onChange: (value: EquipmentTemporalExpression) => void;
  disabled?: boolean;
  labelPrefix?: string;
  allowedBasis?: EquipmentTemporalExpressionBasis[];
  calendarContext?: {
    timeZone: string;
    dstGapPolicy: EquipmentDstGapPolicy;
    dstOverlapPolicy: EquipmentDstOverlapPolicy;
  };
  lockCalendarContext?: boolean;
  hideCalendarContext?: boolean;
}

const ELAPSED_UNITS = [
  { value: 'SECOND', label: '秒' },
  { value: 'MINUTE', label: '分钟' },
  { value: 'HOUR', label: '小时' },
  { value: 'DAY', label: '天（连续时长）' },
] as const;

const CALENDAR_UNITS = [
  { value: 'DAY', label: '日历天' },
  { value: 'WEEK', label: '日历周' },
  { value: 'MONTH', label: '日历月' },
  { value: 'QUARTER', label: '日历季度' },
  { value: 'YEAR', label: '日历年' },
] as const;

export const createElapsedExpression = (): EquipmentElapsedTemporalExpression => ({
  basis: 'ELAPSED_DURATION',
  value: 1,
  unit: 'DAY',
  monthEndPolicy: null,
  timeZone: null,
  dstGapPolicy: null,
  dstOverlapPolicy: null,
});

export const createCalendarExpression = (): EquipmentCalendarTemporalExpression => ({
  basis: 'CALENDAR_PERIOD',
  value: 1,
  unit: 'MONTH',
  monthEndPolicy: 'CLAMP',
  timeZone: 'Asia/Shanghai',
  dstGapPolicy: 'SHIFT_FORWARD',
  dstOverlapPolicy: 'EARLIER_OFFSET',
});

const TemporalExpressionEditor: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  labelPrefix = '周期',
  allowedBasis = ['ELAPSED_DURATION', 'CALENDAR_PERIOD'],
  calendarContext,
  lockCalendarContext = false,
  hideCalendarContext = false,
}) => {
  const changeBasis = (basis: EquipmentTemporalExpressionBasis) => {
    if (basis === 'CALENDAR_PERIOD') {
      const next = createCalendarExpression();
      onChange(calendarContext ? { ...next, ...calendarContext } : next);
      return;
    }
    onChange(createElapsedExpression());
  };

  const isCalendar = value.basis === 'CALENDAR_PERIOD';
  const showsMonthEnd = isCalendar && ['MONTH', 'QUARTER', 'YEAR'].includes(value.unit);

  return (
    <div className="esc-temporal-editor">
      <WxbSelect
        label={`${labelPrefix}计算基准`}
        value={value.basis}
        disabled={disabled}
        options={[
          { value: 'ELAPSED_DURATION', label: '连续经过时长（elapsed）' },
          { value: 'CALENDAR_PERIOD', label: '日历期间（calendar）' },
        ].filter((option) => allowedBasis.includes(option.value as EquipmentTemporalExpressionBasis))}
        onChange={(basis) => changeBasis(basis as EquipmentTemporalExpressionBasis)}
      />
      <WxbInputNumber
        label={`${labelPrefix}数值`}
        min={1}
        precision={isCalendar ? 0 : 3}
        value={value.value}
        disabled={disabled}
        onChange={(amount) => onChange({ ...value, value: typeof amount === 'number' ? amount : 1 })}
      />
      <WxbSelect
        label={`${labelPrefix}单位`}
        value={value.unit}
        disabled={disabled}
        options={isCalendar ? [...CALENDAR_UNITS] : [...ELAPSED_UNITS]}
        onChange={(unit) => {
          if (value.basis === 'ELAPSED_DURATION') {
            onChange({ ...value, unit: unit as typeof value.unit });
            return;
          }
          const nextUnit = unit as EquipmentCalendarTemporalExpression['unit'];
          onChange({
            ...value,
            unit: nextUnit,
            monthEndPolicy: ['MONTH', 'QUARTER', 'YEAR'].includes(nextUnit)
              ? value.monthEndPolicy ?? 'CLAMP'
              : null,
          });
        }}
      />

      {isCalendar && !hideCalendarContext && (
        <>
          <WxbInput
            label={`${labelPrefix}时区`}
            value={value.timeZone}
            disabled={disabled || lockCalendarContext}
            placeholder="例如 Asia/Shanghai"
            helpText={lockCalendarContext ? '继承周期策略时区。' : '使用 IANA 时区名称；日历边界按该时区解释。'}
            onChange={(event) => onChange({ ...value, timeZone: event.target.value })}
          />
          <WxbSelect
            label="夏令时缺口策略"
            value={value.dstGapPolicy}
            disabled={disabled || lockCalendarContext}
            options={[
              { value: 'SHIFT_FORWARD', label: '顺延到首个有效时刻' },
              { value: 'SKIP', label: '跳过本次' },
              { value: 'ERROR', label: '报错并阻断' },
            ]}
            onChange={(policy) => onChange({ ...value, dstGapPolicy: policy as EquipmentDstGapPolicy })}
          />
          <WxbSelect
            label="夏令时重叠策略"
            value={value.dstOverlapPolicy}
            disabled={disabled || lockCalendarContext}
            options={[
              { value: 'EARLIER_OFFSET', label: '取较早偏移' },
              { value: 'LATER_OFFSET', label: '取较晚偏移' },
            ]}
            onChange={(policy) => onChange({ ...value, dstOverlapPolicy: policy as EquipmentDstOverlapPolicy })}
          />
        </>
      )}
      {showsMonthEnd && (
        <WxbSelect
          label="月末策略"
          value={value.monthEndPolicy ?? undefined}
          disabled={disabled}
          placeholder="选择月末策略"
          options={[
            { value: 'CLAMP', label: '截到目标月最后一天' },
            { value: 'PRESERVE_END_OF_MONTH', label: '若锚点为月末则保持月末' },
            { value: 'ROLL_FORWARD', label: '顺延超出天数' },
            { value: 'SKIP', label: '目标日期不存在则跳过' },
          ]}
          onChange={(policy) => onChange({
            ...value,
            monthEndPolicy: policy as EquipmentMonthEndPolicy,
          })}
        />
      )}
    </div>
  );
};

export default TemporalExpressionEditor;
